import type { Ton } from '../commun.js'

/**
 * SUPERVISION — BRIQUES PURES DES VUES DE DOMAINE (vidéo, audiovisuel,
 * bâtiment), 21/10/2026. Aucune dépendance à React ni au réseau : testable.
 *
 * Les formes ci-dessous sont celles de Sonde (contrat v1 : VideoDoc,
 * LightingDoc, SoundDoc, PowerDoc, EventListDoc…), relayées TELLES QUELLES
 * par Gestion. On n'y recalcule rien que Sonde ait déjà dit : on range, on
 * nomme, on colore avec le code unique de `commun.tsx`.
 */

/* ═══════════════ Formes communes de Sonde ═══════════════ */

export interface Collecteur {
  ok?: boolean | null
  stale?: boolean | null
  heartbeat_age_s?: number | null
  code?: string | null
  detail_fr?: string | null
}

/** État d'une source dans une vue de domaine (`sourcesJson`). */
export interface SourceDomaine {
  code: string
  label: string
  status: string
  status_label?: string | null
  since?: string | null
  last_success_at?: string | null
  last_error_code?: string | null
  last_error_label?: string | null
  consecutive_failures?: number | null
  data_age_s?: number | null
}

/** Mesure déclarée par une famille (`s_generic`), étiquette et unité du catalogue de Sonde. */
export interface Generique {
  entity_key: string
  metric: string
  label: string
  unit: string | null
  value: number | null
  uid: string | null
  /** Nom de l'entité à la source (porte « ACC-001-STAFF ») ; absent avant le contrat de83e779. */
  entity_label?: string | null
  at: string
  data_age_s: number | null
}

export interface EtatAppareil {
  display_state: string
  display_label: string
  greyed?: boolean
  cause?: string | null
  cause_label?: string | null
  cause_uid?: string | null
  since?: string | null
  observed_at?: string | null
  data_age_s?: number | null
  flapping?: boolean
  exception?: { kind: string; reason: string; until: string | null } | null
}

/** Appareil en bref (`deviceBrief`). */
export interface AppareilBref {
  uid: string
  display_name: string
  kind: string
  family: string
  family_label?: string | null
  zone_code: string | null
  zone_detail?: string | null
  criticality?: string | null
  state: EtatAppareil
}

/** Alimentation d'une salle (`zoneCards[].power`). */
export interface AlimSalle {
  state: string
  since: string | null
  label_fr: string
  situation: string
  source?: string | null
}

export interface SalleDomaine {
  code: string
  label: string
  power: AlimSalle
  operating?: boolean | null
}

export interface EvenementSonde {
  id: number | string
  at: string
  kind: string
  kind_label?: string | null
  severity: string
  uid?: string | null
  display_name?: string | null
  zone_code?: string | null
  entity_key?: string | null
  source_code?: string | null
  payload?: unknown
}

export interface ListeEvenements { items: EvenementSonde[] }

/** Seuil effectif (`ThresholdView`). */
export interface Seuil { warn: number | null; crit: number | null; direction: 'above' | 'below'; unit: string | null }

/* ═══════════════ Gardes de forme ═══════════════ */

export const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)
export const liste = (x: unknown): x is unknown[] => Array.isArray(x)

/** Une vue de domaine : objet, `sources` en liste, et chacune des listes demandées. */
export function estVueDomaine(x: unknown, listes: string[]): x is Record<string, unknown> {
  if (!objet(x) || !liste(x['sources'])) return false
  return listes.every((k) => liste(x[k]))
}

export function estListeEvenements(x: unknown): x is ListeEvenements {
  return objet(x) && liste(x['items']) && (x['items'] as unknown[]).every((e) => objet(e) && typeof e['kind'] === 'string' && typeof e['at'] === 'string')
}

export function estListeAppareils(x: unknown): x is { items: AppareilBref[]; total?: number | null } {
  return objet(x) && liste(x['items']) && (x['items'] as unknown[]).every((d) => objet(d) && typeof d['uid'] === 'string' && objet(d['state']))
}

/** Liste « À valider » de Sonde : on ne lit que les compteurs et le genre proposé (jamais adresses ni MAC). */
export interface ListeDecouvertes { items: { suggested?: { kind?: string | null } | null }[]; counts?: { pending?: number | null } | null; next_cursor?: string | null }
export function estListeDecouvertes(x: unknown): x is ListeDecouvertes {
  return objet(x) && liste(x['items'])
}

/* ═══════════════ Tons ═══════════════ */

/** Statut d'une source : une source indisponible ou non configurée est GRISE, jamais rouge. */
export const TON_SOURCE: Record<string, Ton> = { OK: 'ok', DEGRADED: 'warn', UNAVAILABLE: 'gris', NOT_CONFIGURED: 'gris', DISABLED: 'gris' }
export const LIBELLE_SOURCE: Record<string, string> = {
  OK: 'Lecture réussie', DEGRADED: 'Dégradée', UNAVAILABLE: 'Indisponible', NOT_CONFIGURED: 'Non configurée', DISABLED: 'Désactivée',
}

/** Niveau d'une mesure face à son seuil (`MeasureLevel`) ; null = on ne sait pas → gris. */
export const TON_MESURE: Record<string, Ton> = { ok: 'ok', warning: 'warn', critical: 'err' }
export const tonMesure = (l: string | null | undefined): Ton => (l ? TON_MESURE[l] ?? 'gris' : 'gris')

/* ═══════════════ Textes ═══════════════ */

/**
 * Garde-fou des suggestions refusées par Hugo (§14) : tout texte qui en
 * reprendrait une n'est pas affiché. Les écrans vidéo, audiovisuel et
 * bâtiment n'émettent aucun conseil ; ce filtre protège ce que Sonde relaie.
 */
const INTERDITS: RegExp[] = [
  /\bminrate\b/i,
  /802\.11r\b/i,
  /r2-plonge[^.]*\b(puissance|canal|channel|power)\b|\b(puissance|canal)\b[^.]*r2-plonge/i,
  /\b(bpdu|stp)\b[^.]*\b(activer|désactiver|desactiver|garde|guard|conseil|recommand)/i,
  /leave\s+session/i,
  /mediamtx[^.]*\btcp\b|\btcp\b[^.]*mediamtx/i,
  /renomm[a-zé]*[^.]*\b(voie|voies|v5)\b|\b(voie|voies|v5)\b[^.]*renomm/i,
  /\bntfy\b/i,
]

export function texteAutorise(t: string | null | undefined): string | null {
  if (!t) return null
  return INTERDITS.some((r) => r.test(t)) ? null : t
}

/** Le texte d'un événement : `payload.message_fr` de Sonde (conséquence d'abord), sinon son libellé. */
export function texteEvenement(e: EvenementSonde): string {
  const p = objet(e.payload) ? e.payload : null
  const m = p && typeof p['message_fr'] === 'string' ? p['message_fr'] : null
  return texteAutorise(m) ?? texteAutorise(e.kind_label ?? null) ?? e.kind
}

/** Débit en octets par seconde (`bytes_*_bps`) → « 3,1 Mbit/s » ; « — » si absent. */
export function fmtDebitOctets(bps: number | null | undefined): string {
  if (bps === null || bps === undefined || !Number.isFinite(bps)) return '—'
  const bits = bps * 8
  if (bits >= 1_000_000) return `${(bits / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mbit/s`
  return `${Math.round(bits / 1000).toLocaleString('fr-FR')} kbit/s`
}

/** Nombre avec décimales maîtrisées, « — » si absent (jamais 0 à la place d'un vide). */
export function fmtDecimal(n: number | null | undefined, unite = '', chiffres = 1): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  const t = n.toLocaleString('fr-FR', { maximumFractionDigits: chiffres })
  return unite ? `${t} ${unite}` : t
}

/** Go → « 4 To » au-delà de 1000 Go. */
export function fmtCapacite(go: number | null | undefined): string {
  if (go === null || go === undefined || !Number.isFinite(go)) return '—'
  return go >= 1000 ? `${(go / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} To` : `${Math.round(go).toLocaleString('fr-FR')} Go`
}

/** 1/0 de Sonde en booléen ; tout le reste = inconnu. */
export const bool01 = (v: number | null | undefined): boolean | null => (v === 1 ? true : v === 0 ? false : null)

/** Âge le plus ancien d'un lot de mesures (la fraîcheur d'un bloc est celle de sa mesure la plus vieille). */
export function agePlusAncien(ages: (number | null | undefined)[]): number | null {
  let out: number | null = null
  for (const a of ages) if (typeof a === 'number' && Number.isFinite(a) && (out === null || a > out)) out = a
  return out
}

/** Nombre de propositions « À valider » dont le genre suggéré commence par l'un des préfixes. */
export function compterADecider(d: ListeDecouvertes | null, prefixes: string[]): number | null {
  if (!d) return null
  return d.items.filter((i) => {
    const k = i.suggested?.kind
    return typeof k === 'string' && prefixes.some((p) => k.startsWith(p))
  }).length
}

/** Lien vers la fiche d'un appareil, route produite par Sonde. */
export const lienAppareil = (uid: string) => `/infrastructure/appareils/${encodeURIComponent(uid)}`
export const lienIncident = (id: string) => `/infrastructure/incidents/${encodeURIComponent(id)}`
export const lienSalle = (code: string) => `/infrastructure/salles/${encodeURIComponent(code)}`
export const LIEN_A_VALIDER = '/infrastructure/registre'
